import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import useSystemFeedback from '@app/hooks/useSystemFeedback';
import { ERROR_DOMAIN, ERROR_PATH } from '../../../../constants/errorConstants';
import ProductCard from '../../../common/product/ProductCard';
import useAnalytics from '../../../../hooks/useAnalytics';
import { STATUS } from '../../../../constants/commonConstants';
import { getPartName, isDev, isEmpty } from '@app/utils/commonUtils';
import { PCC_RELEASE_HEADLESS_PDP_FLAG } from '@app/constants/featureFlags';
import endpoints from '@app/constants/endpoints';
import pdpStyles from '../styles.module.scss';
import Conditional from '@app/components/common/Conditional';

const ProductRelatives = ({
  filter,
  id,
  productId,
  title,
  gaListName,
  isFitmentLoading,
  getFitmentCheck,
  didFitmentCallResolve,
  getFitmentResult,
  priceCallStatus
}) => {
  const { fireProductImpressionEvent } = useAnalytics();

  const currency = useSelector(s => s.dealerCustomer?.currency);
  const formattedZeroPrice = useSelector(
    s => s.dealerCustomer?.formattedZeroPrice
  );
  const isGstAvailable = useSelector(s => s.dealerCustomer?.isGstAvailable);
  const merchandisingAssociations = useSelector(
    s => s.products.byId?.[productId]?.merchandisingAssociations
  );
  const model = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const priceAndAvailability = useSelector(
    s => s.products.priceAndAvailability
  );

  const isPriceResolved = [STATUS.REJECTED, STATUS.RESOLVED].includes(
    priceCallStatus
  );
  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const relatedParts = merchandisingAssociations?.filter(filter);
  const shouldRender = Array.isArray(relatedParts) && relatedParts.length > 0;

  const isPDPHeadlessFeatureFlagEnable =
    useSelector(s => s.featureFlag?.[PCC_RELEASE_HEADLESS_PDP_FLAG]) ?? false;
  const esiteURL = useSelector(s => s.common?.esiteURL);

  const getProductDetails = useCallback(
    (part, index) => ({
      availability: priceAndAvailability?.[part.partNumber]?.availability,
      brand: part.manufacturer,
      category: part.categoryPath?.split('/'),
      checkFitment: getFitmentCheck(part.partNumber),
      partNumber: part.partNumber,
      name: part.name,
      position: index + 1,
      price: priceAndAvailability?.[part.partNumber]?.unformattedUnitPrice,
      productWeight: priceAndAvailability?.[part.partNumber]?.weight,
      quantity: 1,
      relatedModel: model
    }),
    [getFitmentCheck, model, priceAndAvailability]
  );

  const getProductClickDetails = (part, index) => [
    {
      ...getProductDetails(part, index),
      list: gaListName
    }
  ];

  const impressionFired = useRef(false);

  const shouldFireImpressionEvent =
    !impressionFired.current &&
    !isEmpty(relatedParts) &&
    (isPriceResolved || isCatCorp) &&
    (didFitmentCallResolve || !serialNumber);

  useEffect(() => {
    if (shouldFireImpressionEvent) {
      impressionFired.current = true;
      const productsDetails = relatedParts.map(getProductDetails);
      fireProductImpressionEvent(gaListName, productsDetails);
    }
  }, [
    fireProductImpressionEvent,
    gaListName,
    getProductDetails,
    relatedParts,
    shouldFireImpressionEvent
  ]);

  const { clearError, setError } = useSystemFeedback();

  const handleAddToCartError = useCallback(
    error => {
      if (error) {
        setError(ERROR_DOMAIN.PDP, ERROR_PATH.MAIN, error);
      } else {
        clearError(ERROR_DOMAIN.PDP, ERROR_PATH.MAIN);
      }
    },
    [clearError, setError]
  );

  //Position static required for Popup to stand out of scrollable div
  return (
    <Conditional test={shouldRender}>
      <section className="py-3" id={id}>
        <h2 className="h3 display-3">{title}</h2>
        <div
          className={` d-flex position-static pt-2 pb-3 ${pdpStyles['overflow-x-auto']} w-100`}
        >
          {relatedParts?.map((part, index) => {
            const URL = isDev()
              ? endpoints.PRODUCT_DISPLAY_PDP_LINK + part.productURL
              : part.productURL;
            const urlBasedonFeatureFlag = isPDPHeadlessFeatureFlagEnable
              ? `${esiteURL}/product/${part.partNumber}`
              : URL;
            return (
              <ProductCard
                brand={part.manufacturer}
                currency={currency}
                fitmentEnabled={part.isFilterByEquipmentEnabled}
                formattedZeroPrice={formattedZeroPrice}
                gaListName={gaListName}
                handleAddToCartError={handleAddToCartError}
                hasSpecial={!!part.specials}
                imageUrl={part.imageURL}
                isGstAvailable={isGstAvailable}
                key={part.partNumber}
                name={getPartName(part.partNumber, part.name)}
                partNumber={part.partNumber}
                priceCallStatus={priceCallStatus}
                productClickDetails={getProductClickDetails(part, index)}
                productId={part.partNumber}
                url={urlBasedonFeatureFlag}
                variant="relation"
                isFitmentLoading={isFitmentLoading}
                didFitmentCallResolve={didFitmentCallResolve}
                getFitmentResult={getFitmentResult}
              />
            );
          })}
        </div>
      </section>
    </Conditional>
  );
};

ProductRelatives.propTypes = {
  /**
   * Ga list name to particular type
   */
  gaListName: PropTypes.string,
  /**
   * A filter to use to find merchandising associations of a particular type
   */
  filter: PropTypes.func.isRequired,
  /**
   * An id to be applied to the page section
   */
  id: PropTypes.string.isRequired,
  /**
   * The ID of a product to display merchandising associations for
   */
  productId: PropTypes.string,
  /**
   * A section title
   */
  title: PropTypes.string.isRequired,
  /**
   * Boolean for the fitment call called in useFitmentCheck hook
   */
  isFitmentLoading: PropTypes.bool,
  /**
   * fitment check called in useFitmentCheck hook
   */
  getFitmentCheck: PropTypes.func,
  /**
   * Boolean for the fitment call called in useFitmentCheck hook
   */
  didFitmentCallResolve: PropTypes.bool,
  /**
   * fitment result called in useFitmentCheck hook
   */
  getFitmentResult: PropTypes.func,
  /**
   * Status for the P&A call
   */
  priceCallStatus: PropTypes.string.isRequired
};

export default ProductRelatives;
